<template>
  <div :class="embedded ? '' : 'app-container'">
    <div v-if="!embedded" class="page-title">
      <h2>{{ $t('controleInterne.supervision.title') }}</h2>
    </div>

    <el-card :class="embedded ? '' : 'box-card'">
      <div class="filter-container">
        <el-select
          v-model="query.campagne_agricole_id"
          class="mr-15 filter-item"
          style="width: 220px;"
          :placeholder="$t('recolte.campagne')"
          @change="handleFilter"
        >
          <el-option
            v-for="campagne in campagnes"
            :key="campagne.id"
            :label="campagne.nom"
            :value="campagne.id"
          />
        </el-select>

        <el-select
          v-model="query.statut"
          clearable
          class="mr-15 filter-item"
          style="width: 180px;"
          :placeholder="$t('controleInterne.supervision.statut')"
          @change="handleFilter"
        >
          <el-option :label="$t('controleInterne.supervision.enAttente')" value="en_attente" />
          <el-option :label="$t('controleInterne.supervision.approuve')" value="approuve" />
          <el-option :label="$t('controleInterne.supervision.rejete')" value="rejete" />
          <el-option :label="$t('controleInterne.supervision.aCorriger')" value="a_corriger" />
        </el-select>

        <el-input
          v-model="query.keyword"
          :placeholder="$t('general.keyword')"
          class="wd-200 mr-15 filter-item"
          @keyup.enter.native="handleFilter"
        />
        <el-button v-waves class="filter-item" type="primary" icon="el-icon-search" @click="handleFilter">
          {{ $t('general.search') }}
        </el-button>
      </div>

      <el-table v-loading="loading" :data="list" border fit highlight-current-row style="width: 100%">
        <el-table-column align="center" :label="$t('general.code')" min-width="120">
          <template slot-scope="scope">
            <span>{{ scope.row.code }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" :label="$t('parcelle.parcelle')" min-width="120">
          <template slot-scope="scope">
            <span>{{ scope.row.parcelle_code }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" :label="$t('producteur.producteur')" min-width="180">
          <template slot-scope="scope">
            <span>{{ scope.row.producteur }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" :label="$t('controleInterne.supervision.etapes')" min-width="220">
          <template slot-scope="scope">
            <el-tag
              v-for="etape in scope.row.etapes_resume"
              :key="etape.numero"
              size="mini"
              :type="etape.is_completed ? 'success' : 'info'"
              style="margin: 2px;"
            >
              {{ etape.numero }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column align="center" :label="$t('controleInterne.supervision.nc')" width="80">
          <template slot-scope="scope">
            <span>{{ scope.row.nb_non_conformites }}</span>
          </template>
        </el-table-column>

        <el-table-column align="center" :label="$t('controleInterne.supervision.statut')" width="130">
          <template slot-scope="scope">
            <el-tag :type="statutTagType(scope.row.statut_supervision)" size="mini">
              {{ statutLabel(scope.row.statut_supervision) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column align="center" :label="$t('general.actions')" width="120">
          <template slot-scope="scope">
            <el-button type="primary" size="small" @click="openSupervision(scope.row)">
              {{ $t('controleInterne.supervision.review') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <pagination
        v-if="total > 0"
        :total="total"
        :page.sync="query.page"
        :limit.sync="query.limit"
        @pagination="getList"
      />
    </el-card>

    <el-dialog
      :title="$t('controleInterne.supervision.reviewTitle', { code: selectedRow ? selectedRow.code : '' })"
      :visible.sync="dialogVisible"
      width="80%"
      top="5vh"
    >
      <div v-if="selectedRow" v-loading="detailsLoading">
        <el-descriptions :column="2" border size="small">
          <el-descriptions-item :label="$t('parcelle.parcelle')">{{ selectedRow.parcelle_code }}</el-descriptions-item>
          <el-descriptions-item :label="$t('producteur.producteur')">{{ selectedRow.producteur }}</el-descriptions-item>
          <el-descriptions-item :label="$t('controleInterne.supervision.nc')">{{ selectedRow.nb_non_conformites }}</el-descriptions-item>
          <el-descriptions-item :label="$t('controleInterne.supervision.statut')">
            {{ statutLabel(selectedRow.statut_supervision) }}
          </el-descriptions-item>
        </el-descriptions>

        <h4 style="margin-top: 20px;">{{ $t('controleInterne.supervision.etapesDetail') }}</h4>
        <el-table :data="selectedRow.etapes_resume" border size="small" style="margin-bottom: 20px;">
          <el-table-column prop="numero" :label="$t('controleInterne.etapes.ordre')" width="70" />
          <el-table-column prop="libelle" :label="$t('general.libelle')" />
          <el-table-column prop="date_controle" :label="$t('controleInterne.supervision.dateControle')" width="120" />
          <el-table-column prop="statut_parcelle" :label="$t('controleInterne.supervision.statutParcelle')" width="100" />
          <el-table-column :label="$t('general.status')" width="90">
            <template slot-scope="scope">
              <el-tag :type="scope.row.is_completed ? 'success' : 'info'" size="mini">
                {{ scope.row.is_completed ? $t('controleInterne.supervision.termine') : $t('controleInterne.supervision.enCours') }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>

        <div v-if="questionCategories.length">
          <h4>{{ $t('controleInterne.supervision.questionsReponses') }}</h4>
          <el-collapse v-model="activeCategory" accordion style="margin-bottom: 20px;">
            <el-collapse-item
              v-for="(categorie, index) in questionCategories"
              :key="categorie.libelle + index"
              :name="String(index)"
            >
              <template slot="title">
                <span>{{ categorie.libelle }}</span>
                <el-tag size="mini" type="info" style="margin-left: 10px;">
                  {{ countAnsweredQuestions(categorie) }}/{{ categorie.questions.length }}
                </el-tag>
              </template>
              <el-table :data="categorie.questions" border size="small">
                <el-table-column type="index" :label="'#'" width="50" />
                <el-table-column :label="$t('general.libelle')" min-width="280">
                  <template slot-scope="scope">
                    <span>{{ scope.row.libelle }}</span>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('controleInterne.supervision.reponse')" width="140">
                  <template slot-scope="scope">
                    <el-tag
                      v-if="scope.row.reponse_status"
                      size="mini"
                      :type="reponseTagType(scope.row.reponse)"
                    >
                      {{ formatReponse(scope.row.reponse) }}
                    </el-tag>
                    <span v-else class="text-muted">{{ $t('controleInterne.supervision.noReponse') }}</span>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('controleInterne.supervision.commentaire')" min-width="180">
                  <template slot-scope="scope">
                    <span>{{ scope.row.commentaire || $t('controleInterne.supervision.sansCommentaire') }}</span>
                  </template>
                </el-table-column>
              </el-table>
            </el-collapse-item>
          </el-collapse>
        </div>

        <div v-if="details && details.non_conformites && details.non_conformites.length">
          <h4>{{ $t('controleInterne.supervision.nonConformites') }}</h4>
          <el-table :data="details.non_conformites" border size="small" style="margin-bottom: 20px;">
            <el-table-column prop="point_controle_numero" :label="$t('question.numero')" width="90" />
            <el-table-column prop="non_conformite" :label="$t('controleInterne.supervision.nonConformite')" />
            <el-table-column prop="mesure_corrective" :label="$t('controleInterne.supervision.mesureCorrective')" />
          </el-table>
        </div>

        <el-form :model="approbationForm" label-position="top">
          <el-form-item :label="$t('controleInterne.supervision.decision')">
            <el-radio-group v-model="approbationForm.statut">
              <el-radio label="approuve">{{ $t('controleInterne.supervision.approuve') }}</el-radio>
              <el-radio label="a_corriger">{{ $t('controleInterne.supervision.aCorriger') }}</el-radio>
              <el-radio label="rejete">{{ $t('controleInterne.supervision.rejete') }}</el-radio>
            </el-radio-group>
          </el-form-item>
          <el-form-item :label="$t('controleInterne.supervision.commentaire')">
            <el-input v-model="approbationForm.commentaire" type="textarea" :rows="3" />
          </el-form-item>
        </el-form>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ $t('general.cancel') }}</el-button>
        <el-button type="primary" :loading="saving" @click="submitApprobation">
          {{ $t('general.save') }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Pagination from '@/components/Pagination';
import Resource from '@/api/resource';
import waves from '@/directive/waves';
import { getSupervisionList, getControleInterneDetails, approuverSuperviseur } from '@/api/controleInterne';

const campagneResource = new Resource('campagne_agricoles');

export default {
  name: 'ControleInterneSupervisionList',
  components: { Pagination },
  directives: { waves },
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      list: [],
      campagnes: [],
      total: 0,
      loading: false,
      saving: false,
      detailsLoading: false,
      dialogVisible: false,
      selectedRow: null,
      details: null,
      activeCategory: '0',
      query: {
        page: 1,
        limit: 20,
        keyword: '',
        statut: 'en_attente',
        campagne_agricole_id: null,
      },
      approbationForm: {
        statut: 'approuve',
        commentaire: '',
      },
    };
  },
  created() {
    this.loadCampagnes();
  },
  computed: {
    questionCategories() {
      if (!this.details || !Array.isArray(this.details.categories)) {
        return [];
      }

      return this.details.categories.filter(
        categorie => Array.isArray(categorie.questions) && categorie.questions.length > 0
      );
    },
  },
  methods: {
    statutLabel(statut) {
      const map = {
        en_attente: this.$t('controleInterne.supervision.enAttente'),
        approuve: this.$t('controleInterne.supervision.approuve'),
        rejete: this.$t('controleInterne.supervision.rejete'),
        a_corriger: this.$t('controleInterne.supervision.aCorriger'),
      };
      return map[statut] || statut;
    },
    statutTagType(statut) {
      if (statut === 'approuve') {
        return 'success';
      }
      if (statut === 'rejete') {
        return 'danger';
      }
      if (statut === 'a_corriger') {
        return 'warning';
      }
      return 'info';
    },
    async loadCampagnes() {
      const { data } = await campagneResource.list({ limit: 100 });
      this.campagnes = data || [];
      try {
        const { data: current } = await campagneResource.sublist('/en_cours');
        this.query.campagne_agricole_id = current && current.id ? current.id : (this.campagnes[0] ? this.campagnes[0].id : null);
      } catch (error) {
        this.query.campagne_agricole_id = this.campagnes[0] ? this.campagnes[0].id : null;
      }
      this.getList();
    },
    async getList() {
      this.loading = true;
      try {
        const response = await getSupervisionList(this.query);
        this.list = response.data || [];
        this.total = response.meta ? response.meta.total : this.list.length;
      } finally {
        this.loading = false;
      }
    },
    handleFilter() {
      this.query.page = 1;
      this.getList();
    },
    async openSupervision(row) {
      this.selectedRow = row;
      this.approbationForm = {
        statut: row.approbation_superviseur && row.approbation_superviseur.statut !== 'en_attente'
          ? row.approbation_superviseur.statut
          : 'approuve',
        commentaire: row.approbation_superviseur ? (row.approbation_superviseur.commentaire || '') : '',
      };
      this.details = null;
      this.activeCategory = '0';
      this.dialogVisible = true;
      this.detailsLoading = true;
      try {
        const response = await getControleInterneDetails(row.id);
        this.details = response.data || null;
      } catch (error) {
        console.error(error);
        this.$message.error(this.$t('general.errorOccurred'));
      } finally {
        this.detailsLoading = false;
      }
    },
    countAnsweredQuestions(categorie) {
      if (!categorie || !Array.isArray(categorie.questions)) {
        return 0;
      }

      return categorie.questions.filter(question => question.reponse_status).length;
    },
    formatReponse(reponse) {
      if (reponse === null || reponse === undefined || reponse === '') {
        return this.$t('controleInterne.supervision.noReponse');
      }

      return String(reponse);
    },
    reponseTagType(reponse) {
      const value = String(reponse || '').toLowerCase();
      if (value === 'oui' || value === 'yes' || value === 'conforme') {
        return 'success';
      }
      if (value === 'non' || value === 'no' || value === 'non conforme') {
        return 'danger';
      }
      return 'info';
    },
    async submitApprobation() {
      if (!this.selectedRow) {
        return;
      }
      this.saving = true;
      try {
        await approuverSuperviseur(this.selectedRow.id, this.approbationForm);
        this.$message.success(this.$t('controleInterne.supervision.saved'));
        this.dialogVisible = false;
        this.getList();
      } catch (error) {
        console.error(error);
        this.$message.error(this.$t('general.errorOccurred'));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.text-muted {
  color: #909399;
  font-style: italic;
}
</style>
